import { Stack, Typography, IconButton, Chip } from '@mui/material';
import { useDrag } from '@use-gesture/react';
import React, { useRef } from 'react';
import { Controller } from 'react-hook-form';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { useTranslate } from 'src/locales';
import { ShopItem } from 'src/types/events/shop';

import { CurrencyCodes } from '../../types/users/currency';
import { formatCurrency } from '../../utils/formatters/currency';
import { ExpandableDescription } from './expandable-description';

interface Props {
  shopItems?: ShopItem[];
  control?: any;
  isMobile?: boolean;
  currency?: CurrencyCodes;
  openDrawer?: () => void;
  handleShopIncrease?: (field: any, index: number) => void;
}

const ShopItemsList: React.FC<Props> = ({
  shopItems,
  control,
  isMobile,
  openDrawer,
  currency,
  handleShopIncrease,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslate('common');
  const bind = useDrag(
    ({ movement: [mx], memo = scrollContainerRef.current?.scrollLeft }) => {
      scrollContainerRef.current!.scrollLeft = memo! - mx;
      return memo;
    },
    {
      // Enable horizontal drag only
      axis: 'x',
      // Filter out vertical movements
      filterTaps: true,
    }
  );

  return (
    <Stack
      {...bind()}
      gap={2}
      ref={scrollContainerRef}
      direction="row"
      sx={{
        overflow: 'scroll',
        px: 2,
        pb: 1,
        // Allow vertical touch actions while maintaining horizontal drag
        touchAction: 'pan-y',
        cursor: 'grab',
        // Smooth scrolling
        scrollBehavior: 'smooth',
        // Prevent flickering and enable hardware acceleration
        WebkitOverflowScrolling: 'touch',
        transform: 'translateZ(0)',
        willChange: 'transform',
        '&:active': {
          cursor: 'grabbing',
        },
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      {shopItems &&
        shopItems.map((item, index) => (
          <Stack
            key={index}
            direction="column"
            justifyContent="space-between"
            gap={1}
            sx={{ backgroundColor: '#000', p: 2, minWidth: 200, borderRadius: 2 }}
          >
            <Stack direction="column" justifyContent="flex-start" gap={1}>
              {item?.picture?.url && (
                <Image
                  src={item?.picture?.url}
                  ratio="1/1"
                  width="10rem"
                  height="10rem"
                  borderRadius={1}
                />
              )}
              <Stack py={1} gap={1}>
                <Typography variant="subtitle1">{item?.title}</Typography>
                {item?.description && <ExpandableDescription text={item.description} />}
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">{formatCurrency(item?.net, currency)}</Typography>
              {control && item.quantityLeft && handleShopIncrease ? (
                <Controller
                  name={`shopItems.${index}.quantityInCart`}
                  control={control}
                  render={({ field }) => (
                    <IconButton
                      sx={{ backgroundColor: '#FFD369', p: 0.4 }}
                      onClick={() => {
                        handleShopIncrease(field, index);
                        if (!isMobile && openDrawer) {
                          openDrawer();
                        }
                      }}
                    >
                      <Iconify icon="typcn:plus" color="#000" width={20} height={20} />
                    </IconButton>
                  )}
                />
              ) : (
                <Chip label={t('ticket.sold-out')} variant="outlined" />
              )}
            </Stack>
          </Stack>
        ))}
      {shopItems &&
        !control &&
        shopItems.map((ticket, index) => (
          <Stack
            key={index}
            direction="column"
            justifyContent="space-between"
            sx={{ backgroundColor: '#151515', px: 2, py: 2, minWidth: 200, borderRadius: 2 }}
          >
            <Image src={ticket?.picture?.url} ratio="1/1" />
            {ticket?.description && (
              <Stack py={1}>
                <Typography variant="subtitle1">{ticket?.title}</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ mb: 1 }}>
                  {ticket?.description}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">{formatCurrency(ticket?.price, currency)}</Typography>
            </Stack>
          </Stack>
        ))}
    </Stack>
  );
};

export default ShopItemsList;
